import { useMemo } from 'react'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { Checkbox, Chip, DataTable } from '@/components/ui'
import type { SopRow } from '../api/types'
import { SopStatusChip } from './SopStatusChip'

export interface SopTableProps {
  rows: SopRow[]
  onSelect: (row: SopRow) => void
  /** Authoring surfaces (sop-management) show the status column since
   *  drafts/archived rows are visible there; the read-only library
   *  omits it (everything shown there is published). */
  showStatus?: boolean
  /** SOP Management: what each SOP is missing against the template. */
  showGaps?: boolean
  moduleId?: string
  /** Row selection (checkboxes), keyed by SOP id. */
  selection?: RowSelectionState
  onSelectionChange?: (s: RowSelectionState) => void
}

export function SopTable({
  rows,
  onSelect,
  showStatus = false,
  showGaps = false,
  moduleId,
  selection,
  onSelectionChange,
}: SopTableProps) {
  const selectable = Boolean(onSelectionChange)
  const columns = useMemo<ColumnDef<SopRow, unknown>[]>(() => {
    const cols: ColumnDef<SopRow, unknown>[] = [
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
        cell: (ctx) => {
          const gaps = ctx.row.original.gaps
          return (
            <span className="flex items-center gap-2">
              <span className="text-role-data-primary">{ctx.row.original.title}</span>
              {showGaps && gaps.length ? (
                <Chip variant="warning" dot title={`Missing: ${gaps.join(', ')}`}>
                  {gaps.length} to complete
                </Chip>
              ) : null}
            </span>
          )
        },
      },
      {
        id: 'category',
        header: 'Category',
        accessorKey: 'category',
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (r) => r.owner ?? '',
        cell: (ctx) => ctx.row.original.owner ?? <span className="text-tertiary">—</span>,
      },
      {
        id: 'version',
        header: 'Version',
        accessorFn: (r) => r.version ?? '',
        cell: (ctx) => ctx.row.original.version ?? <span className="text-tertiary">—</span>,
      },
      {
        id: 'updated_at',
        header: 'Updated',
        accessorKey: 'updated_at',
        cell: (ctx) =>
          ctx.row.original.updated_at ? new Date(ctx.row.original.updated_at).toLocaleDateString() : '—',
      },
    ]
    if (showStatus) {
      cols.push({
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (ctx) => <SopStatusChip status={ctx.row.original.status} />,
      })
    }
    if (selectable) {
      cols.unshift({
        id: '__select',
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all SOPs"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: (ctx) => (
          // The row opens the SOP on click; ticking the box must not.
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              aria-label={`Select ${ctx.row.original.title}`}
              checked={ctx.row.getIsSelected()}
              onChange={ctx.row.getToggleSelectedHandler()}
            />
          </div>
        ),
        enableSorting: false,
        size: 44,
      })
    }
    return cols
  }, [showStatus, showGaps, selectable])

  return (
    <DataTable
      data={rows}
      columns={columns}
      getRowId={(r) => r.id}
      onRowClick={onSelect}
      moduleId={moduleId}
      selection={selection}
      onSelectionChange={onSelectionChange}
      maxHeight="100%"
      minTableWidth={800}
    />
  )
}
